<script lang="ts" setup>
import AddressDataService from '@/services/AddressDataService'
import type Address from '@/types/Address'
import type ResponseData from '@/types/ResponseData'
import type FakturacneUdaje from '@/types/FakturacneUdaje'
import UserDataService from '@/services/UserDataService'
import type User from '@/types/User'
import HeadquartersDataService from '@/services/HeadquartersDataService'
import type Headquarters from '@/types/Headquarters'
import CompanyDataService from '@/services/CompanyDataService'
import type Company from '@/types/Company'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'
import CompanyMemberDataService from '@/services/CompanyMemberDataService'
import type CompanyMember from '@/types/CompanyMember'
import { onMounted, ref, computed } from 'vue';
import store from '@/store'
import OrderDataService from '@/services/OrderDataService'
import {
    MinusIcon,
    PlusIcon
} from 'lucide-vue-next'

// UI primitives
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  CardAction,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuGroup,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog'
import { Alert, AlertTitle, AlertDescription, AlertAction } from '@/components/ui/alert'
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import { Input } from '@/components/ui/input'

const currentOrder = computed(() => store.state.currentOrder);

const currentOrderAddress = ref({} as Address);
const currentCompanyAddress = ref({} as Address);
const currentUserAddress = ref({} as Address);
const currentFakturacneUdaje = ref({} as FakturacneUdaje);
const currentUser = ref({} as User);
const currentCompany = ref({} as Company);
const currentHeadquarters = ref({} as Headquarters);
const currentCompanyMembers = ref([] as CompanyMember[]);
const items = ref([] as any[]);
const tab = ref(1);
const companySubStatuses = ref([] as any);
const currentCompanyUpdateOrder = ref({} as any);
const sharesTransfers = ref([] as any[]);
const actual_company = ref({} as any);
const updated_company = ref({} as any);
const headquartersTypes = ref([] as any);

function currentTab(tabNumber: number) {
    tab.value = tabNumber
}

async function getHeadquartersTypes() {
    HeadquartersDataService.getHeadquartersTypes()
        .then((response: ResponseData) => {
            headquartersTypes.value = response.data.data
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
        })
}

async function getUser(id: any) {
    await UserDataService.getUserById(id)
        .then(async (response: ResponseData) => {
            currentUser.value = response.data.data
            if (currentUser.value.address_id > 0)
                await getUserAddress(currentUser.value.address_id)
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
        })
}

async function getCompany(id: any) {
    await CompanyDataService.getCompanyById(id)
        .then((response: ResponseData) => {
            currentCompany.value = response.data.data
            getCompanyHeadquarters(currentCompany.value.headquarters_id)
            getCompanyMembers(currentCompany.value.id)
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
        })
}

async function getCompanyMembers(id: any) {
    await CompanyMemberDataService.getAllCompanyMembersForCompany(id)
        .then((response: ResponseData) => {
            currentCompanyMembers.value = response.data.data
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
        })
}

async function getOrderAddress(id: any) {
    await AddressDataService.getAddressById(id)
        .then((response: ResponseData) => {
            currentOrderAddress.value = response.data.data
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
        })
}

async function getUserAddress(id: any) {
    await AddressDataService.getAddressById(id)
        .then((response: ResponseData) => {
            currentUserAddress.value = response.data.data
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
        })
}

async function getCompanyAddress(id: any) {
    await AddressDataService.getAddressById(id)
        .then((response: ResponseData) => {
            currentCompanyAddress.value = response.data.data
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
        })
}

async function getCompanyHeadquarters(id: any) {
    await HeadquartersDataService.getHeadquartersById(id)
        .then(async (response: ResponseData) => {
            currentHeadquarters.value = response.data.data
            if (currentHeadquarters.value.address_id > 0)
                await getCompanyAddress(currentHeadquarters.value.address_id)
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
        })
}

async function getFakturacneUdaje(id: any) {
    await UserDataService.getFakturacneUdajeById(id)
        .then(async (response: ResponseData) => {
            currentFakturacneUdaje.value = response.data.data
            if (currentFakturacneUdaje.value.address_id > 0)
                await getOrderAddress(currentFakturacneUdaje.value.address_id)
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
        })
}

async function getCompanySubStatuses() {
    CompanyDataService.getCompanySubStatuses()
        .then((response: ResponseData) => {
            companySubStatuses.value = response.data.data
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
        })
}

async function updateSubStatus() {
    await CompanyDataService.update(currentCompany.value.id, currentCompany.value)
        .then(() => {
            toast.success('Stav žiadosti bol úspešne zmenený');
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message);
        });
}

async function getCompanyUpdateOrder() {
    OrderDataService.getCompanyUpdateOrder(currentOrder.value.id)
        .then((response: ResponseData) => {
            currentCompanyUpdateOrder.value = response.data.data
            const parsedTransfers = JSON.parse(currentCompanyUpdateOrder.value.sharesTransfers)
            // Handle both old flat format (SharesTransfers[]) and new nested format (SharesTransfers[][])
            sharesTransfers.value = Array.isArray(parsedTransfers[0]) ? parsedTransfers.flat() : parsedTransfers
            actual_company.value = JSON.parse(currentCompanyUpdateOrder.value.actual_company)
            updated_company.value = JSON.parse(currentCompanyUpdateOrder.value.updated_company)
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
        })
}

async function refreshData() {
    items.value = JSON.parse(currentOrder.value.items)

    await getFakturacneUdaje(currentOrder.value.fakturacne_udaje_id)
    await getUser(currentOrder.value.user_id)
    if (currentOrder.value.company_id !== null) {
        await getCompany(currentOrder.value.company_id)
    }
    await getCompanySubStatuses();

    if (currentOrder.value.order_type == "cupdate") {
        await getCompanyUpdateOrder();
    }

    await getHeadquartersTypes();
}

onMounted(async () => {
    await refreshData();
})
</script>

<template>
    <div class="max-w-(--breakpoint-2xl) mx-auto">
        <!-- Header Card -->
        <Card class="mb-6 flex flex-col gap-4">
            <CardHeader class="flex flex-row items-center gap-3">
                <CardAction>
                    <svg class="size-8 text-brand-500" fill="none" stroke="currentColor" stroke-width="2"
                        viewBox="0 0 24 24" title="Zmena firmy">
                        <rect x="4" y="4" width="16" height="16" rx="3" stroke="currentColor" stroke-width="2"
                            fill="none" />
                        <path d="M8 12h8M8 16h8M8 8h8" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                    </svg>
                </CardAction>
                <div>
                    <CardTitle class="text-2xl font-bold text-gray-900 mb-0 dark:text-white/90">Zmena firmy (s.r.o.)</CardTitle>
                    <CardDescription class="text-lg font-semibold text-gray-700 dark:text-gray-300">
                        <svg class="size-6 text-gray-400 inline-block" fill="none" stroke="currentColor" stroke-width="2"
                            viewBox="0 0 24 24" title="Názov firmy">
                            <path d="M16 7a4 4 0 0 1-8 0" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round" />
                            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" fill="none" />
                        </svg>
                        {{ currentCompany.name }}
                    </CardDescription>
                </div>
                <CardAction>
                    <span v-if="items.length && items[0].price" class="ml-2 text-xl font-semibold text-brand-600"
                        title="Celková cena">- {{ items[0].price }}€</span>
                </CardAction>
            </CardHeader>
            <CardContent class="flex flex-col gap-2 mt-2">
<FormKit type="select" v-model="currentCompany.sub_status" name="sub_status" id="sub_status"
                    :classes="{ outer: 'flex flex-col', input: 'mt-1 h-11 w-full rounded-[10px] border border-gray-300 bg-white px-4 text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90' }"
                    @input="updateSubStatus">
                    <option v-for="status in companySubStatuses" :key="status.id" :value="status.id">{{ status.description }}</option>
                </FormKit>
            </CardContent>
        </Card>

        <!-- Tabs -->
        <div class="flex flex-row justify-center gap-2 mb-6">
            <Button
                @click="currentTab(1)"
                type="button"
                :variant="tab === 1 ? 'default' : 'outline'"
                title="Faktúrované služby">
                <svg class="size-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <rect x="4" y="4" width="16" height="16" rx="3" stroke="currentColor" stroke-width="2"
                        fill="none" />
                    <path d="M8 12h8" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                </svg>
                Faktúrované služby
            </Button>
            <Button
                @click="currentTab(2)"
                type="button"
                :variant="tab === 2 ? 'default' : 'outline'"
                title="Zmeny v spoločnosti">
                <svg class="size-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <path d="M12 4v16m8-8H4" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                </svg>
                Zmeny v spoločnosti
            </Button>
            <Button
                @click="currentTab(3)"
                type="button"
                :variant="tab === 3 ? 'default' : 'outline'"
                title="Prevod akcií">
                <svg class="size-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <path d="M16 17l-4 4m0 0l-4-4m4 4V3" stroke="currentColor" stroke-width="2"
                        stroke-linecap="round" />
                </svg>
                Prevod akcií
            </Button>
        </div>

        <Card>
            <!-- Faktúrované služby -->
            <div v-if="tab === 1">
                <CardContent class="mb-4 flex items-center gap-3 p-4 rounded-lg bg-gray-50 border border-gray-100 dark:bg-white/3 dark:border-gray-800">
                    <svg class="size-6 text-brand-500" fill="none" stroke="currentColor" stroke-width="2"
                        viewBox="0 0 24 24" title="Súčet cien">
                        <rect x="4" y="4" width="16" height="16" rx="3" stroke="currentColor" stroke-width="2"
                            fill="none" />
                        <path d="M8 12h8" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                    </svg>
                    <span class="font-semibold text-gray-900 dark:text-white/90">Suma všetkých služieb:</span>
                    <span class="ml-auto text-xl font-bold text-brand-700">{{items.reduce((sum, i) => sum +
                        (isNaN(parseFloat(i.price ?? '0')) ? 0 : parseFloat(i.price ?? '0')), 0).toFixed(2)}} €</span>
                </CardContent>
                <CardContent class="shadow-xs overflow-hidden sm:rounded-md">
                    <div class="px-4 py-5 bg-white sm:p-6 flex flex-col gap-4 dark:bg-white/3">
                        <div v-for="(item, index) in items" :key="index"
                            class="transition hover:bg-brand-50 rounded-lg p-3 group border border-gray-100 dark:border-gray-800">
                            <div class="grid grid-cols-9 gap-6 items-center">
                                <div class="col-span-1 text-center self-center text-gray-400 font-semibold">#{{ index +
                                    1
                                }}</div>
                                <div class="col-span-3 sm:col-span-3">
                                    <label for="description"
                                        class="block text-sm font-medium text-gray-700 dark:text-gray-300">Popis</label>
                                    <div>{{ item.description }}</div>
                                </div>
                                <div class="col-span-3 sm:col-span-3">
                                    <label for="amount" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Suma</label>
                                    <Input v-model="item.price" name="amount" id="amount"
                                        class="mt-1" />
                                </div>
                                <div class="col-span-2 sm:col-span-2">
                                    <label for="amount" class="block text-sm font-medium text-gray-700 dark:text-gray-300">DPH</label>
                                    <Input v-model="item.price_vat" name="amount" id="amount"
                                        class="mt-1" />
                                </div>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </div>

            <!--Súčasná spoločnosť-->
            <div v-if="tab === 2">
                <!-- Legend -->
                <Alert variant="default" class="flex flex-wrap gap-3 mb-6 p-3 bg-gray-50 rounded-lg border border-gray-200 text-sm dark:bg-white/3 dark:border-gray-800">
                    <AlertTitle class="inline-flex items-center gap-1.5 font-medium text-gray-600 dark:text-gray-300">
                        <Badge variant="light" color="gray" size="sm" class="size-3 p-0" />
                        Aktuálny stav
                    </AlertTitle>
                    <AlertTitle class="inline-flex items-center gap-1.5 font-medium text-brand-700">
                        <Badge variant="light" color="primary" size="sm" class="size-3 p-0" />
                        Nový stav (zmena)
                    </AlertTitle>
                    <AlertTitle class="inline-flex items-center gap-1.5 font-medium text-green-700">
                        <Badge variant="light" color="success" size="sm" class="size-3 p-0" />
                        Vyvolanie konateľa
                    </AlertTitle>
                    <AlertTitle class="inline-flex items-center gap-1.5 font-medium text-red-700">
                        <Badge variant="light" color="error" size="sm" class="size-3 p-0" />
                        Odvolanie konateľa
                    </AlertTitle>
                </Alert>

                <div class="flex flex-col gap-6">
                    <!-- Základné údaje -->
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <!-- Aktuálna -->
                        <Card class="bg-gray-50 border border-gray-200 overflow-hidden dark:bg-white/3 dark:border-gray-800">
                            <CardHeader class="px-4 py-2.5 bg-gray-200 flex items-center gap-2 dark:bg-gray-700">
                                <CardAction>
                                    <Badge variant="light" color="gray" size="sm" class="size-2.5 p-0" />
                                </CardAction>
                                <CardTitle class="font-semibold text-gray-700 text-sm mb-0 dark:text-gray-300">Aktuálna spoločnosť</CardTitle>
                            </CardHeader>
                            <CardContent class="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div class="flex flex-col">
                                    <label class="block text-xs font-medium text-gray-600 mb-1 dark:text-gray-300">Meno spoločnosti</label>
                                    <Input v-model="actual_company.obchodne_meno" id="act_obchodne_meno"
                                        />
                                </div>
                                <div class="flex flex-col">
                                    <label class="block text-xs font-medium text-gray-600 mb-1 dark:text-gray-300">IČO</label>
                                    <Input v-model="actual_company.ico" id="act_ico"
                                        />
                                </div>
                                <div class="flex flex-col sm:col-span-2">
                                    <label class="block text-xs font-medium text-gray-600 mb-1 dark:text-gray-300">Právna forma</label>
                                    <Input v-model="actual_company.pravna_forma" id="act_pravna_forma"
                                        />
                                </div>
                                <div class="flex flex-col sm:col-span-2">
                                    <label class="block text-xs font-medium text-gray-600 mb-1 dark:text-gray-300">Konanie menom spoločnosti</label>
                                    <Input v-model="actual_company.konanie_menom_spolocnosti" id="act_konanie"
                                        type="textarea"
                                        />
                                </div>
                                <div class="flex flex-col sm:col-span-2" v-if="actual_company.zakladne_imanie">
                                    <label class="block text-xs font-medium text-gray-600 mb-1 dark:text-gray-300">Základné imanie</label>
                                    <Input v-model="actual_company.zakladne_imanie" id="act_zakladne_imanie"
                                        />
                                </div>
                            </CardContent>
                        </Card>

                        <!-- Zmenená -->
                        <Card class="bg-brand-50 border border-brand-200 overflow-hidden">
                            <CardHeader class="px-4 py-2.5 bg-brand-200 flex items-center gap-2">
                                <CardAction>
                                    <Badge variant="light" color="primary" size="sm" class="size-2.5 p-0" />
                                </CardAction>
                                <CardTitle class="font-semibold text-brand-800 text-sm mb-0">Zmenená spoločnosť</CardTitle>
                            </CardHeader>
                            <CardContent class="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div class="flex flex-col">
                                    <label class="block text-xs font-medium text-brand-700 mb-1">Meno spoločnosti</label>
                                    <Input v-model="updated_company.obchodne_meno" id="upd_obchodne_meno"
                                        />
                                </div>
                                <div class="flex flex-col">
                                    <label class="block text-xs font-medium text-brand-700 mb-1">IČO</label>
                                    <Input v-model="updated_company.ico" id="upd_ico"
                                        />
                                </div>
                                <div class="flex flex-col sm:col-span-2">
                                    <label class="block text-xs font-medium text-brand-700 mb-1">Právna forma</label>
                                    <Input v-model="updated_company.pravna_forma" id="upd_pravna_forma"
                                        />
                                </div>
                                <div class="flex flex-col sm:col-span-2">
                                    <label class="block text-xs font-medium text-brand-700 mb-1">Konanie menom spoločnosti</label>
                                    <Input v-model="updated_company.konanie_menom_spolocnosti" id="upd_konanie"
                                        type="textarea"
                                        />
                                </div>
                                <!-- Základné imanie changes -->
                                <div class="flex flex-col sm:col-span-2" v-if="updated_company.zakladne_imanie?.length > 0">
                                    <label class="block text-xs font-semibold text-brand-700 mb-2">Základné imanie — zmeny</label>
                                    <div v-for="(imanie, index) in updated_company.zakladne_imanie" :key="index">
                                        <div v-if="imanie.change_for" class="flex flex-wrap items-center gap-2 mb-2 p-2 rounded-sm bg-white border border-brand-100 dark:bg-white/3">
                                            <component :is="imanie.operation == '+' ? PlusIcon : MinusIcon"
                                                :class="imanie.operation == '+' ? 'size-5 text-green-500' : 'size-5 text-red-500'" />
                                            <div class="flex flex-col flex-1 min-w-0">
                                                <label class="block text-xs font-medium text-gray-600 mb-1 dark:text-gray-300">Zmena pre</label>
                                                <Input v-model="imanie.change_for"
                                                    />
                                            </div>
                                            <div class="flex flex-col flex-1 min-w-0">
                                                <label class="block text-xs font-medium text-gray-600 mb-1 dark:text-gray-300">Hodnota</label>
                                                <Input v-model="imanie.value"
                                                    />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    <!-- Sídlo -->
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <!-- Aktuálne sídlo -->
                        <Card class="bg-gray-50 border border-gray-200 overflow-hidden dark:bg-white/3 dark:border-gray-800">
                            <CardHeader class="px-4 py-2.5 bg-gray-200 flex items-center gap-2 dark:bg-gray-700">
                                <CardAction>
                                    <Badge variant="light" color="gray" size="sm" class="size-2.5 p-0" />
                                </CardAction>
                                <CardTitle class="font-semibold text-gray-700 text-sm mb-0 dark:text-gray-300">Sídlo — aktuálne</CardTitle>
                            </CardHeader>
                            <CardContent class="p-4 grid grid-cols-2 gap-3">
                                <div class="flex flex-col">
                                    <label class="block text-xs font-medium text-gray-600 mb-1 dark:text-gray-300">Platné od</label>
                                    <Input v-model="actual_company.sidlo.since" id="act_since"
                                        />
                                </div>
                                <div class="flex flex-col">
                                    <label class="block text-xs font-medium text-gray-600 mb-1 dark:text-gray-300">PSČ</label>
                                    <Input v-model="actual_company.sidlo.zip" id="act_zip"
                                        />
                                </div>
                                <div class="flex flex-col">
                                    <label class="block text-xs font-medium text-gray-600 mb-1 dark:text-gray-300">Mesto</label>
                                    <Input v-model="actual_company.sidlo.city" id="act_city"
                                        />
                                </div>
                                <div class="flex flex-col">
                                    <label class="block text-xs font-medium text-gray-600 mb-1 dark:text-gray-300">Ulica</label>
                                    <Input v-model="actual_company.sidlo.street" id="act_street"
                                        />
                                </div>
                                <div class="flex flex-col">
                                    <label class="block text-xs font-medium text-gray-600 mb-1 dark:text-gray-300">Číslo</label>
                                    <Input v-model="actual_company.sidlo.number" id="act_number"
                                        />
                                </div>
                            </CardContent>
                        </Card>

                        <!-- Nové sídlo -->
                        <Card class="bg-brand-50 border border-brand-200 overflow-hidden">
                            <CardHeader class="px-4 py-2.5 bg-brand-200 flex items-center gap-2">
                                <CardAction>
                                    <Badge variant="light" color="primary" size="sm" class="size-2.5 p-0" />
                                </CardAction>
                                <CardTitle class="font-semibold text-brand-800 text-sm mb-0">Sídlo — nové</CardTitle>
                            </CardHeader>
                            <CardContent class="p-4 grid grid-cols-2 gap-3">
                                <template v-if="updated_company.sidlo.address != undefined">
                                    <div class="flex flex-col col-span-2">
                                        <label class="block text-xs font-medium text-brand-700 mb-1">Názov sídla</label>
                                        <Input v-model="updated_company.sidlo.name" id="upd_sidlo_name"
                                            />
                                    </div>
                                    <div class="flex flex-col">
                                        <label class="block text-xs font-medium text-brand-700 mb-1">PSČ</label>
                                        <Input v-model="updated_company.sidlo.address.psc" id="upd_zip"
                                            />
                                    </div>
                                    <div class="flex flex-col">
                                        <label class="block text-xs font-medium text-brand-700 mb-1">Mesto</label>
                                        <Input v-model="updated_company.sidlo.address.city" id="upd_city"
                                            />
                                    </div>
                                    <div class="flex flex-col">
                                        <label class="block text-xs font-medium text-brand-700 mb-1">Ulica</label>
                                        <Input v-model="updated_company.sidlo.address.street" id="upd_street"
                                            />
                                    </div>
                                    <div class="flex flex-col">
                                        <label class="block text-xs font-medium text-brand-700 mb-1">Číslo</label>
                                        <Input v-model="updated_company.sidlo.address.street_number" id="upd_number"
                                            />
                                    </div>
                                    <div class="flex flex-col">
                                        <label class="block text-xs font-medium text-brand-700 mb-1">Číslo 2</label>
                                        <Input v-model="updated_company.sidlo.address.street_number2" id="upd_number2"
                                            />
                                    </div>
                                </template>
                                <template v-else>
                                    <div class="flex flex-col">
                                        <label class="block text-xs font-medium text-brand-700 mb-1">PSČ</label>
                                        <Input :v-model="updated_company.sidlo.zip ?? updated_company.sidlo.psc" id="upd_zip2"
                                            />
                                    </div>
                                    <div class="flex flex-col">
                                        <label class="block text-xs font-medium text-brand-700 mb-1">Mesto</label>
                                        <Input v-model="updated_company.sidlo.city" id="upd_city2"
                                            />
                                    </div>
                                    <div class="flex flex-col">
                                        <label class="block text-xs font-medium text-brand-700 mb-1">Ulica</label>
                                        <Input v-model="updated_company.sidlo.street" id="upd_street2"
                                            />
                                    </div>
                                    <div class="flex flex-col">
                                        <label class="block text-xs font-medium text-brand-700 mb-1">Číslo</label>
                                        <Input :v-model="updated_company.sidlo.number ?? updated_company.sidlo.street_number" id="upd_number3"
                                            />
                                    </div>
                                    <template v-if="updated_company.sidlo?.headquarters_type">
                                        <div class="flex flex-col">
                                            <label class="block text-xs font-medium text-brand-700 mb-1">Typ nehnuteľnosti</label>
                                            <FormKit type="select" v-model="updated_company.sidlo.headquarters_type" name="druh_priestoru"
                                                :classes="{ outer: 'flex flex-col', input: 'h-11 w-full rounded-[10px] border border-gray-300 bg-white px-4 text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90' }">
                                                <option class="text-gray-500 dark:text-gray-400">Vyberte druh priestoru</option>
                                                <option v-for="hqType in headquartersTypes" :key="hqType.id" :value="hqType.id">{{ hqType.description }}</option>
                                            </FormKit>
                                        </div>
                                        <div class="flex flex-col" v-if="updated_company.sidlo?.owner_name">
                                            <label class="block text-xs font-medium text-brand-700 mb-1">Majiteľ nehnuteľnosti</label>
                                            <Input v-model="updated_company.sidlo.owner_name" id="upd_owner"
                                                />
                                        </div>
                                    </template>
                                </template>
                            </CardContent>
                        </Card>
                    </div>

                    <!-- Konatelia -->
                    <div v-if="actual_company.konatelia?.length > 0 || updated_company.konatelia?.length > 0"
                        class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <!-- Aktuálni konatelia -->
                        <Card class="bg-gray-50 border border-gray-200 overflow-hidden dark:bg-white/3 dark:border-gray-800">
                            <CardHeader class="px-4 py-2.5 bg-gray-200 flex items-center gap-2 dark:bg-gray-700">
                                <CardAction>
                                    <Badge variant="light" color="gray" size="sm" class="size-2.5 p-0" />
                                </CardAction>
                                <CardTitle class="font-semibold text-gray-700 text-sm mb-0 dark:text-gray-300">Konatelia — aktuálni</CardTitle>
                            </CardHeader>
                            <CardContent class="p-4 flex flex-col gap-3">
                                <div v-for="(konatel, index) in actual_company.konatelia" :key="'act-kon-' + index"
                                    class="border border-gray-200 rounded-lg p-3 bg-white dark:border-gray-800 dark:bg-white/3">
                                    <p class="text-xs font-semibold text-gray-500 mb-2 dark:text-gray-400">#{{ index }}</p>
                                    <div class="grid grid-cols-2 gap-2">
                                        <div class="flex flex-col">
                                            <label class="block text-xs font-medium text-gray-600 mb-1 dark:text-gray-300">Meno</label>
                                            <Input v-model="konatel.name"
                                                />
                                        </div>
                                        <div class="flex flex-col">
                                            <label class="block text-xs font-medium text-gray-600 mb-1 dark:text-gray-300">Aktívny od</label>
                                            <Input v-model="konatel.since"
                                                />
                                        </div>
                                        <div class="flex flex-col" v-if="konatel.new_konatel_date_from">
                                            <label class="block text-xs font-medium text-green-600 mb-1">Dátum vyvolania</label>
                                            <Input v-model="konatel.new_konatel_date_from"
                                                />
                                        </div>
                                        <div class="flex flex-col" v-if="konatel.cancel_from_date">
                                            <label class="block text-xs font-medium text-red-600 mb-1">Dátum odvolania</label>
                                            <Input v-model="konatel.cancel_from_date"
                                                />
                                        </div>
                                        <div class="flex flex-col">
                                            <label class="block text-xs font-medium text-gray-600 mb-1 dark:text-gray-300">PSČ</label>
                                            <Input v-model="konatel.zip"
                                                />
                                        </div>
                                        <div class="flex flex-col">
                                            <label class="block text-xs font-medium text-gray-600 mb-1 dark:text-gray-300">Mesto</label>
                                            <Input v-model="konatel.city"
                                                />
                                        </div>
                                        <div class="flex flex-col">
                                            <label class="block text-xs font-medium text-gray-600 mb-1 dark:text-gray-300">Ulica</label>
                                            <Input v-model="konatel.street"
                                                />
                                        </div>
                                        <div class="flex flex-col">
                                            <label class="block text-xs font-medium text-gray-600 mb-1 dark:text-gray-300">Číslo</label>
                                            <Input v-model="konatel.number"
                                                />
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <!-- Noví/zmenení konatelia -->
                        <Card class="bg-brand-50 border border-brand-200 overflow-hidden">
                            <CardHeader class="px-4 py-2.5 bg-brand-200 flex items-center gap-2">
                                <CardAction>
                                    <Badge variant="light" color="primary" size="sm" class="size-2.5 p-0" />
                                </CardAction>
                                <CardTitle class="font-semibold text-brand-800 text-sm mb-0">Konatelia — zmeny</CardTitle>
                            </CardHeader>
                            <CardContent class="p-4 flex flex-col gap-3">
                                <template v-for="(konatel, index) in updated_company.konatelia" :key="'upd-kon-' + index">
                                    <div v-if="konatel != null"
                                        class="border rounded-lg p-3 bg-white dark:bg-white/3"
                                        :class="konatel.new_konatel_date_from ? 'border-green-400' : konatel.cancel_from_date ? 'border-red-400' : 'border-brand-200'">
                                        <div class="flex items-center justify-between mb-2">
                                            <p class="text-xs font-semibold text-gray-500 dark:text-gray-400">#{{ index }}</p>
                                            <Badge v-if="konatel.new_konatel_date_from"
                                                variant="light" color="success" size="sm"
                                                class="rounded-full font-semibold">Vyvolanie</Badge>
                                            <Badge v-else-if="konatel.cancel_from_date"
                                                variant="light" color="error" size="sm"
                                                class="rounded-full font-semibold">Odvolanie</Badge>
                                        </div>
                                        <div class="grid grid-cols-2 gap-2">
                                            <div class="flex flex-col">
                                                <label class="block text-xs font-medium text-brand-700 mb-1">Meno</label>
                                                <Input v-model="konatel.first_name"
                                                    />
                                            </div>
                                            <div class="flex flex-col">
                                                <label class="block text-xs font-medium text-brand-700 mb-1">Priezvisko</label>
                                                <Input v-model="konatel.last_name"
                                                    />
                                            </div>
                                            <div class="flex flex-col" v-if="konatel.new_konatel_date_from">
                                                <label class="block text-xs font-medium text-green-700 mb-1">Dátum vyvolania</label>
                                                <Input v-model="konatel.new_konatel_date_from"
                                                    />
                                            </div>
                                            <div class="flex flex-col" v-if="konatel.cancel_from_date">
                                                <label class="block text-xs font-medium text-red-700 mb-1">Dátum odvolania</label>
                                                <Input v-model="konatel.cancel_from_date"
                                                    />
                                            </div>
                                            <div class="flex flex-col">
                                                <label class="block text-xs font-medium text-brand-700 mb-1">Pohlavie</label>
                                                <Input v-model="konatel.gender"
                                                    />
                                            </div>
                                            <div class="flex flex-col">
                                                <label class="block text-xs font-medium text-brand-700 mb-1">Rodné číslo</label>
                                                <Input v-model="konatel.rodne_cislo"
                                                    />
                                            </div>
                                            <div class="flex flex-col">
                                                <label class="block text-xs font-medium text-brand-700 mb-1">Dátum narodenia</label>
                                                <Input v-model="konatel.date_of_birth"
                                                    />
                                            </div>
                                            <div class="flex flex-col">
                                                <label class="block text-xs font-medium text-brand-700 mb-1">Titul pred menom</label>
                                                <Input v-model="konatel.title_before"
                                                    />
                                            </div>
                                            <div class="flex flex-col">
                                                <label class="block text-xs font-medium text-brand-700 mb-1">Titul za menom</label>
                                                <Input v-model="konatel.title_after"
                                                    />
                                            </div>
                                            <div class="flex flex-col">
                                                <label class="block text-xs font-medium text-brand-700 mb-1">PSČ</label>
                                                <Input v-model="konatel.psc"
                                                    />
                                            </div>
                                            <div class="flex flex-col">
                                                <label class="block text-xs font-medium text-brand-700 mb-1">Mesto</label>
                                                <Input v-model="konatel.city"
                                                    />
                                            </div>
                                            <div class="flex flex-col">
                                                <label class="block text-xs font-medium text-brand-700 mb-1">Ulica</label>
                                                <Input v-model="konatel.street"
                                                    />
                                            </div>
                                            <div class="flex flex-col">
                                                <label class="block text-xs font-medium text-brand-700 mb-1">Číslo</label>
                                                <Input v-model="konatel.street_number"
                                                    />
                                            </div>
                                            <div class="flex flex-col">
                                                <label class="block text-xs font-medium text-brand-700 mb-1">Krajina</label>
                                                <Input v-model="konatel.country"
                                                    />
                                            </div>
                                        </div>
                                    </div>
                                    <div v-else class="text-sm text-gray-400 italic px-3 py-2 bg-white border border-gray-100 rounded-lg dark:bg-white/3 dark:border-gray-800">#{{ index }} — Bez zmien</div>
                                </template>
                            </CardContent>
                        </Card>
                    </div>

                    <!-- Spoločníci -->
                    <div v-if="actual_company.spolocnici?.length > 0 || (Array.isArray(updated_company.spolocnici) && updated_company.spolocnici.length > 0)"
                        class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <!-- Aktuálni spoločníci -->
                        <Card class="bg-gray-50 border border-gray-200 overflow-hidden dark:bg-white/3 dark:border-gray-800">
                            <CardHeader class="px-4 py-2.5 bg-gray-200 flex items-center gap-2 dark:bg-gray-700">
                                <CardAction>
                                    <Badge variant="light" color="gray" size="sm" class="size-2.5 p-0" />
                                </CardAction>
                                <CardTitle class="font-semibold text-gray-700 text-sm mb-0 dark:text-gray-300">Spoločníci — aktuálni</CardTitle>
                            </CardHeader>
                            <CardContent class="p-4 flex flex-col gap-3">
                                <div v-for="(spolocnik, index) in actual_company.spolocnici" :key="'act-spol-' + index"
                                    class="border border-gray-200 rounded-lg p-3 bg-white dark:border-gray-800 dark:bg-white/3">
                                    <p class="text-xs font-semibold text-gray-500 mb-2 dark:text-gray-400">#{{ index }}</p>
                                    <div class="grid grid-cols-2 gap-2">
                                        <div class="flex flex-col">
                                            <label class="block text-xs font-medium text-gray-600 mb-1 dark:text-gray-300">Meno</label>
                                            <Input v-model="spolocnik.name"
                                                />
                                        </div>
                                        <div class="flex flex-col">
                                            <label class="block text-xs font-medium text-gray-600 mb-1 dark:text-gray-300">Obchodné meno</label>
                                            <Input v-model="spolocnik.obchodne_meno"
                                                />
                                        </div>
                                        <div class="flex flex-col">
                                            <label class="block text-xs font-medium text-gray-600 mb-1 dark:text-gray-300">IČO</label>
                                            <Input v-model="spolocnik.ico"
                                                />
                                        </div>
                                        <div class="flex flex-col">
                                            <label class="block text-xs font-medium text-gray-600 mb-1 dark:text-gray-300">Aktívny od</label>
                                            <Input v-model="spolocnik.since"
                                                />
                                        </div>
                                        <div class="flex flex-col">
                                            <label class="block text-xs font-medium text-gray-600 mb-1 dark:text-gray-300">Podiel v spoločnosti</label>
                                            <Input v-model="spolocnik.podiel_v_spolocnosti"
                                                />
                                        </div>
                                        <div class="flex flex-col">
                                            <label class="block text-xs font-medium text-gray-600 mb-1 dark:text-gray-300">Výška vkladu</label>
                                            <Input v-model="spolocnik.vyska_vkladu"
                                                />
                                        </div>
                                        <div class="flex flex-col">
                                            <label class="block text-xs font-medium text-gray-600 mb-1 dark:text-gray-300">Rozsah splatenia</label>
                                            <Input v-model="spolocnik.rozsah_splatenia_vkladu"
                                                />
                                        </div>
                                        <div class="flex flex-col">
                                            <label class="block text-xs font-medium text-gray-600 mb-1 dark:text-gray-300">PSČ</label>
                                            <Input v-model="spolocnik.zip"
                                                />
                                        </div>
                                        <div class="flex flex-col">
                                            <label class="block text-xs font-medium text-gray-600 mb-1 dark:text-gray-300">Mesto</label>
                                            <Input v-model="spolocnik.city"
                                                />
                                        </div>
                                        <div class="flex flex-col">
                                            <label class="block text-xs font-medium text-gray-600 mb-1 dark:text-gray-300">Ulica</label>
                                            <Input v-model="spolocnik.street"
                                                />
                                        </div>
                                        <div class="flex flex-col">
                                            <label class="block text-xs font-medium text-gray-600 mb-1 dark:text-gray-300">Číslo</label>
                                            <Input v-model="spolocnik.number"
                                                />
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <!-- Noví/zmenení spoločníci -->
                        <Card class="bg-brand-50 border border-brand-200 overflow-hidden">
                            <CardHeader class="px-4 py-2.5 bg-brand-200 flex items-center gap-2">
                                <CardAction>
                                    <Badge variant="light" color="primary" size="sm" class="size-2.5 p-0" />
                                </CardAction>
                                <CardTitle class="font-semibold text-brand-800 text-sm mb-0">Spoločníci — zmeny</CardTitle>
                            </CardHeader>
                            <CardContent class="p-4 flex flex-col gap-3">
                                <template v-for="(spolocnik, index) in updated_company.spolocnici" :key="'upd-spol-' + index">
                                    <div v-if="spolocnik && typeof spolocnik === 'object' && spolocnik.first_name != null"
                                        class="border border-brand-200 rounded-lg p-3 bg-white dark:bg-white/3">
                                        <p class="text-xs font-semibold text-gray-500 mb-2 dark:text-gray-400">#{{ index }}</p>
                                        <div class="grid grid-cols-2 gap-2">
                                            <div class="flex flex-col">
                                                <label class="block text-xs font-medium text-brand-700 mb-1">Meno</label>
                                                <Input v-model="spolocnik.first_name"
                                                    />
                                            </div>
                                            <div class="flex flex-col">
                                                <label class="block text-xs font-medium text-brand-700 mb-1">Priezvisko</label>
                                                <Input v-model="spolocnik.last_name"
                                                    />
                                            </div>
                                            <div class="flex flex-col">
                                                <label class="block text-xs font-medium text-brand-700 mb-1">Aktívny od</label>
                                                <Input v-model="spolocnik.since"
                                                    />
                                            </div>
                                            <div class="flex flex-col">
                                                <label class="block text-xs font-medium text-brand-700 mb-1">Podiel v spoločnosti</label>
                                                <Input v-model="spolocnik.podiel_v_spolocnosti"
                                                    />
                                            </div>
                                            <div class="flex flex-col">
                                                <label class="block text-xs font-medium text-brand-700 mb-1">Výška vkladu</label>
                                                <Input v-model="spolocnik.vyska_vkladu"
                                                    />
                                            </div>
                                            <div class="flex flex-col">
                                                <label class="block text-xs font-medium text-brand-700 mb-1">Rozsah splatenia</label>
                                                <Input v-model="spolocnik.rozsah_splatenia_vkladu"
                                                    />
                                            </div>
                                            <div class="flex flex-col">
                                                <label class="block text-xs font-medium text-brand-700 mb-1">PSČ</label>
                                                <Input v-model="spolocnik.psc"
                                                    />
                                            </div>
                                            <div class="flex flex-col">
                                                <label class="block text-xs font-medium text-brand-700 mb-1">Mesto</label>
                                                <Input v-model="spolocnik.city"
                                                    />
                                            </div>
                                            <div class="flex flex-col">
                                                <label class="block text-xs font-medium text-brand-700 mb-1">Ulica</label>
                                                <Input v-model="spolocnik.street"
                                                    />
                                            </div>
                                            <div class="flex flex-col">
                                                <label class="block text-xs font-medium text-brand-700 mb-1">Číslo</label>
                                                <Input v-model="spolocnik.street_number"
                                                    />
                                            </div>
                                        </div>
                                    </div>
                                    <div v-else class="text-gray-400 italic text-xs py-2 px-3 bg-white border border-gray-100 rounded-lg dark:bg-white/3 dark:border-gray-800">Neplatný alebo prázdny záznam spoločníka</div>
                                </template>
                            </CardContent>
                        </Card>
                    </div>

                    <!-- Prokurista -->
                    <div v-if="actual_company.prokurista?.length > 0 || updated_company.prokurista?.length > 0"
                        class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <!-- Aktuálny prokurista -->
                        <Card class="bg-gray-50 border border-gray-200 overflow-hidden dark:bg-white/3 dark:border-gray-800">
                            <CardHeader class="px-4 py-2.5 bg-gray-200 flex items-center gap-2 dark:bg-gray-700">
                                <CardAction>
                                    <Badge variant="light" color="gray" size="sm" class="size-2.5 p-0" />
                                </CardAction>
                                <CardTitle class="font-semibold text-gray-700 text-sm mb-0 dark:text-gray-300">Prokurista — aktuálny</CardTitle>
                            </CardHeader>
                            <CardContent class="p-4 flex flex-col gap-3">
                                <div v-for="(prokurista, index) in actual_company.prokurista" :key="'act-prok-' + index"
                                    class="border border-gray-200 rounded-lg p-3 bg-white dark:border-gray-800 dark:bg-white/3">
                                    <Input v-model="prokurista.name"
                                        />
                                </div>
                            </CardContent>
                        </Card>

                        <!-- Nový prokurista -->
                        <Card class="bg-brand-50 border border-brand-200 overflow-hidden">
                            <CardHeader class="px-4 py-2.5 bg-brand-200 flex items-center gap-2">
                                <CardAction>
                                    <Badge variant="light" color="primary" size="sm" class="size-2.5 p-0" />
                                </CardAction>
                                <CardTitle class="font-semibold text-brand-800 text-sm mb-0">Prokurista — zmeny</CardTitle>
                            </CardHeader>
                            <CardContent class="p-4 flex flex-col gap-3">
                                <div v-for="(prokurista, index) in updated_company.prokurista" :key="'upd-prok-' + index"
                                    class="border border-brand-200 rounded-lg p-3 bg-white dark:bg-white/3">
                                    <p class="text-xs font-semibold text-gray-500 mb-2 dark:text-gray-400">#{{ index }}</p>
                                    <div class="grid grid-cols-2 gap-2">
                                        <div class="flex flex-col" v-if="prokurista.obchodne_meno">
                                            <label class="block text-xs font-medium text-brand-700 mb-1">Obchodné meno</label>
                                            <Input v-model="prokurista.obchodne_meno"
                                                />
                                        </div>
                                        <div class="flex flex-col">
                                            <label class="block text-xs font-medium text-brand-700 mb-1">Meno</label>
                                            <Input v-model="prokurista.first_name"
                                                />
                                        </div>
                                        <div class="flex flex-col">
                                            <label class="block text-xs font-medium text-brand-700 mb-1">Priezvisko</label>
                                            <Input v-model="prokurista.last_name"
                                                />
                                        </div>
                                        <div class="flex flex-col">
                                            <label class="block text-xs font-medium text-brand-700 mb-1">Pohlavie</label>
                                            <Input v-model="prokurista.gender"
                                                />
                                        </div>
                                        <div class="flex flex-col">
                                            <label class="block text-xs font-medium text-brand-700 mb-1">Rodné číslo</label>
                                            <Input v-model="prokurista.rodne_cislo"
                                                />
                                        </div>
                                        <div class="flex flex-col">
                                            <label class="block text-xs font-medium text-brand-700 mb-1">Dátum narodenia</label>
                                            <Input v-model="prokurista.date_of_birth"
                                                />
                                        </div>
                                        <div class="flex flex-col">
                                            <label class="block text-xs font-medium text-brand-700 mb-1">Titul pred menom</label>
                                            <Input v-model="prokurista.title_before"
                                                />
                                        </div>
                                        <div class="flex flex-col">
                                            <label class="block text-xs font-medium text-brand-700 mb-1">Titul za menom</label>
                                            <Input v-model="prokurista.title_after"
                                                />
                                        </div>
                                        <div class="flex flex-col">
                                            <label class="block text-xs font-medium text-brand-700 mb-1">PSČ</label>
                                            <Input v-model="prokurista.psc"
                                                />
                                        </div>
                                        <div class="flex flex-col">
                                            <label class="block text-xs font-medium text-brand-700 mb-1">Mesto</label>
                                            <Input v-model="prokurista.city"
                                                />
                                        </div>
                                        <div class="flex flex-col">
                                            <label class="block text-xs font-medium text-brand-700 mb-1">Ulica</label>
                                            <Input v-model="prokurista.street"
                                                />
                                        </div>
                                        <div class="flex flex-col">
                                            <label class="block text-xs font-medium text-brand-700 mb-1">Číslo</label>
                                            <Input v-model="prokurista.street_number"
                                                />
                                        </div>
                                        <div class="flex flex-col">
                                            <label class="block text-xs font-medium text-brand-700 mb-1">Krajina</label>
                                            <Input v-model="prokurista.country"
                                                />
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    <!-- Predmety činnosti -->
                    <div v-if="actual_company.predmet_cinnosti?.length > 0 || updated_company.predmet_cinnosti?.length > 0 || updated_company.removed_subject_business?.length > 0"
                        class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <!-- Aktuálne predmety -->
                        <Card class="bg-gray-50 border border-gray-200 overflow-hidden dark:bg-white/3 dark:border-gray-800">
                            <CardHeader class="px-4 py-2.5 bg-gray-200 flex items-center gap-2 dark:bg-gray-700">
                                <CardAction>
                                    <Badge variant="light" color="gray" size="sm" class="size-2.5 p-0" />
                                </CardAction>
                                <CardTitle class="font-semibold text-gray-700 text-sm mb-0 dark:text-gray-300">Predmety činnosti — aktuálne</CardTitle>
                            </CardHeader>
                            <CardContent class="p-4">
                                <div v-for="(predmet, index) in actual_company.predmet_cinnosti" :key="'act-pred-' + index">
                                    <Input v-model="predmet.name"
                                        class="mb-2" />
                                </div>
                            </CardContent>
                        </Card>

                        <!-- Nové/vymazané predmety -->
                        <Card class="bg-brand-50 border border-brand-200 overflow-hidden">
                            <CardHeader class="px-4 py-2.5 bg-brand-200 flex items-center gap-2">
                                <CardAction>
                                    <Badge variant="light" color="primary" size="sm" class="size-2.5 p-0" />
                                </CardAction>
                                <CardTitle class="font-semibold text-brand-800 text-sm mb-0">Predmety činnosti — zmeny</CardTitle>
                            </CardHeader>
                            <CardContent class="p-4 flex flex-col gap-3">
                                <div v-if="updated_company.predmet_cinnosti?.length > 0">
                                    <p class="text-xs font-semibold text-green-700 mb-2">Pridávané predmety:</p>
                                    <ul class="list-disc pl-4 text-sm text-gray-700 space-y-1 dark:text-gray-300">
                                        <li v-for="predmet in updated_company.predmet_cinnosti" :key="predmet">{{ predmet }}</li>
                                    </ul>
                                </div>
                                <div v-if="updated_company.removed_subject_business?.length > 0">
                                    <p class="text-xs font-semibold text-red-700 mb-2">Vymazávané predmety:</p>
                                    <ul class="list-disc pl-4 text-sm text-red-700 space-y-1">
                                        <li v-for="predmet in updated_company.removed_subject_business" :key="predmet">{{ predmet }}</li>
                                    </ul>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    <!-- Iné zmeny -->
                    <div v-if="actual_company.ine_zmeny || updated_company.ine_zmeny"
                        class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Card class="bg-gray-50 border border-gray-200 overflow-hidden dark:bg-white/3 dark:border-gray-800">
                            <CardHeader class="px-4 py-2.5 bg-gray-200 flex items-center gap-2 dark:bg-gray-700">
                                <CardAction>
                                    <Badge variant="light" color="gray" size="sm" class="size-2.5 p-0" />
                                </CardAction>
                                <CardTitle class="font-semibold text-gray-700 text-sm mb-0 dark:text-gray-300">Iné zmeny — aktuálne</CardTitle>
                            </CardHeader>
                            <CardContent class="p-4">
                                <Input v-model="actual_company.ine_zmeny" id="act_ine_zmeny"
                                    type="textarea"
                                    />
                            </CardContent>
                        </Card>
                        <Card class="bg-brand-50 border border-brand-200 overflow-hidden">
                            <CardHeader class="px-4 py-2.5 bg-brand-200 flex items-center gap-2">
                                <CardAction>
                                    <Badge variant="light" color="primary" size="sm" class="size-2.5 p-0" />
                                </CardAction>
                                <CardTitle class="font-semibold text-brand-800 text-sm mb-0">Iné zmeny — nové</CardTitle>
                            </CardHeader>
                            <CardContent class="p-4">
                                <Input v-model="updated_company.ine_zmeny" id="upd_ine_zmeny"
                                    type="textarea"
                                    />
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>
        </Card>

        <!--Prevod akcii -->
        <Card v-if="tab === 3" class="mt-5 md:mt-0 md:col-span-2">
            <CardContent class="shadow-xs overflow-hidden sm:rounded-md">
                <div class="px-4 py-5 bg-white sm:p-6 dark:bg-white/3" v-for="(item, index) in sharesTransfers" :key="index">
                    <div class="flex flex-col gap-6 w-full" v-if="item && !Array.isArray(item)">
                        <div class="flex flex-row w-full">
                            <div class="flex flex-col w-full">
                                <label for="description" class="block text-sm font-bold text-gray-700 dark:text-gray-300">Mena</label>
                                <div>{{ item.currency }}</div>
                            </div>
                            <div class="flex flex-col w-full">
                                <label for="description" class="block text-sm font-bold text-gray-700 dark:text-gray-300">Typ
                                    prevodu</label>
                                <div>{{item.transferType.replace(/([A-Z])/g, (match) => `
                                        ${match}`).replace(/^./, (match) => match.toUpperCase()).trim()}}</div>
                            </div>
                            <div class="flex flex-col w-full">
                                <label for="description" class="block text-sm font-bold text-gray-700 dark:text-gray-300">Suma
                                    prevodu</label>
                                <div>{{ item.amountOfTransfer }}</div>
                            </div>
                            <div class="flex flex-col w-full">
                                <label for="description" class="block text-sm font-bold text-gray-700 dark:text-gray-300">Cena
                                    za prevod</label>
                                <div>{{ item.priceForTransfer }}</div>
                            </div>
                        </div>
                        <div class="flex flex-row border-t-2">
                            <div class="flex flex-col w-full">
                                <label for="sharesfrom" class="block text-sm text-gray-700 font-bold py-4 dark:text-gray-300">Prevod
                                    od</label>
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Meno</label>
                                    <Input v-model="item.sharesFrom.name" id="sharesFrom_name"
                                        class="mt-1" />
                                </div>
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Obchodné
                                        meno</label>
                                    <Input v-model="item.sharesFrom.obchodne_meno" id="sharesto_name"
                                        class="mt-1" />
                                </div>
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">IČO</label>
                                    <Input v-model="item.sharesFrom.ico" id="sharesFrom_ico"
                                        class="mt-1" />
                                </div>
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Titul pred
                                        menom</label>
                                    <Input v-model="item.sharesFrom.title_before" id="sharesFrom_tb"
                                        class="mt-1" />
                                </div>
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Titul za
                                        menom</label>
                                    <Input v-model="item.sharesFrom.title_after" id="sharesFrom_ta"
                                        class="mt-1" />
                                </div>
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Dátum
                                        narodenia</label>
                                    <Input v-model="item.sharesFrom.date_of_birth" id="sharesFrom_dob"
                                        class="mt-1" />
                                </div>
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Rodné
                                        číslo</label>
                                    <Input v-model="item.sharesFrom.rodneCislo" id="sharesFrom_rc"
                                        class="mt-1" />
                                </div>
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Pohlavie</label>
                                    <Input v-model="item.sharesFrom.pohlavie" id="sharesFrom_pohlavie"
                                        class="mt-1" />
                                </div>
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Typ osoby</label>
                                    <Input v-model="item.sharesFrom.typOsoby" id="sharesFrom_type"
                                        class="mt-1" />
                                </div>
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">PSČ</label>
                                    <Input v-model="item.sharesFrom.psc" id="sharesFrom_psc"
                                        class="mt-1" />
                                </div>
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Mesto</label>
                                    <Input v-model="item.sharesFrom.city" id="sharesFrom_psc"
                                        class="mt-1" />
                                </div>
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Ulica</label>
                                    <Input v-model="item.sharesFrom.street" id="sharesFrom_street"
                                        class="mt-1" />
                                </div>

                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Číslo</label>
                                    <Input v-model="item.sharesFrom.streetNumber" id="sharesFrom_psc"
                                        class="mt-1" />
                                </div>
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Štát</label>
                                    <Input v-model="item.sharesFrom.country" id="sharesFrom_country"
                                        class="mt-1" />
                                </div>

                            </div>
                            <div class="flex flex-col w-full">
                                <label for="prijemca"
                                    class="block text-sm font-bold text-gray-700 py-4 dark:text-gray-300">Príjemca</label>
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Meno</label>
                                    <Input v-model="item.sharesTo.name" id="sharesto_name"
                                        class="mt-1" />
                                </div>
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Obchodné
                                        meno</label>
                                    <Input v-model="item.sharesTo.obchodne_meno" id="sharesto_name"
                                        class="mt-1" />
                                </div>
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">IČO</label>
                                    <Input v-model="item.sharesTo.ico" id="sharesto_ico"
                                        class="mt-1" />
                                </div>
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Titul pred
                                        menom</label>
                                    <Input v-model="item.sharesTo.title_before" id="sharesto_tb"
                                        class="mt-1" />
                                </div>
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Titul za
                                        menom</label>
                                    <Input v-model="item.sharesTo.title_after" id="sharesto_ta"
                                        class="mt-1" />
                                </div>
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Dátum
                                        narodenia</label>
                                    <Input v-model="item.sharesTo.date_of_birth" id="sharesto_dob"
                                        class="mt-1" />
                                </div>
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Rodné
                                        číslo</label>
                                    <Input v-model="item.sharesTo.rodneCislo" id="sharesto_rc"
                                        class="mt-1" />
                                </div>
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Pohlavie</label>
                                    <Input v-model="item.sharesTo.pohlavie" id="sharesto_pohlavie"
                                        class="mt-1" />
                                </div>
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Typ osoby</label>
                                    <Input v-model="item.sharesTo.typOsoby" id="sharesto_type"
                                        class="mt-1" />
                                </div>
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">PSČ</label>
                                    <Input v-model="item.sharesTo.psc" id="sharesto_psc"
                                        class="mt-1" />
                                </div>
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Mesto</label>
                                    <Input v-model="item.sharesTo.city" id="sharesto_psc"
                                        class="mt-1" />
                                </div>
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Ulica</label>
                                    <Input v-model="item.sharesTo.street" id="sharesto_street"
                                        class="mt-1" />
                                </div>

                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Číslo</label>
                                    <Input v-model="item.sharesTo.streetNumber" id="sharesto_psc"
                                        class="mt-1" />
                                </div>
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Štát</label>
                                    <Input v-model="item.sharesTo.country" id="sharesto_country"
                                        class="mt-1" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>

        <div class="py-3 bg-gray-50 text-right dark:bg-white/3">
            <Button type="submit">
                Uložiť
            </Button>
        </div>
    </div>
</template>
